import { useEffect, useRef, useState } from "react";
import { api, idr } from "../../lib/api";
import { Sparkles } from "lucide-react";

/**
 * Autocomplete input for RekonSales.
 * Fetches suggestions from /api/rekon-sales/suggest?field=&q= (debounced).
 * Keyboard: ArrowUp/Down navigates, Enter picks, Esc closes.
 */
export default function AutocompleteInput({
  value, onChange, field, placeholder, className = "", inputProps = {},
  onPick,
  // Tiga prop di bawah ditambahkan 15 Sep 2026 (G2) supaya komponen ini bisa
  // dipakai ulang untuk sumber saran LAIN — repo ini sudah menanggung minimal
  // 8 dropdown-saran terpisah, dan inilah satu-satunya yang punya navigasi
  // keyboard + click-outside. Ketiganya OPSIONAL dan bawaannya persis
  // perilaku lama, jadi 3 pemakai di RekonSales.jsx tidak berubah sedikit pun.
  //
  // `fetcher(q)` -> array baris. Tiap baris WAJIB punya `value` (itu yang
  // ditulis ke input saat dipilih); medan lain bebas dan diserahkan ke
  // `renderRow`.
  fetcher,
  // `minChars` menahan permintaan sampai ketikannya cukup panjang. Untuk
  // sumber data yang besar ini BUKAN sekadar penghemat jaringan: tanpa
  // ambang, fokus ke kolom saja sudah menarik seluruh isi daftar.
  minChars = 0,
  renderRow,
  "data-testid": testId,
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [idx, setIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggest = (q) => {
    clearTimeout(debounceRef.current);
    // Ambang huruf diperiksa SEBELUM timer dipasang, dan hasil lama dibuang —
    // kalau tidak, menghapus ketikan sampai di bawah ambang akan menyisakan
    // daftar basi yang terlihat seperti hasil pencarian yang sah.
    if ((q || "").trim().length < minChars) {
      setRows([]); setIdx(-1); setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const baris = fetcher
          ? await fetcher(q || "")
          : (await api.get(`/rekon-sales/suggest?field=${field}&q=${encodeURIComponent(q || "")}`)).data;
        setRows(Array.isArray(baris) ? baris : []);
        setIdx(-1);
      } catch { setRows([]); }
      setLoading(false);
    }, 180);
  };

  const handleChange = (v) => {
    onChange(v);
    fetchSuggest(v);
    if (!open) setOpen(true);
  };

  const handleFocus = () => {
    setOpen(true);
    if (rows.length === 0) fetchSuggest(value);
  };

  // Baris bawaan = bentuk lama (nilai + harga terakhir + hitungan), supaya
  // pemakai lama tidak perlu mengoper apa pun.
  const barisBawaan = (r) => (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-slate-900 truncate font-medium">{r.value}</p>
        {r.last_price > 0 && field !== "imei" && (
          <p className="text-[10px] text-slate-500 font-mono">Terakhir: {idr(r.last_price)}</p>
        )}
      </div>
      <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5 whitespace-nowrap inline-flex items-center gap-1 flex-shrink-0">
        <Sparkles className="w-2.5 h-2.5" /> {r.count}&times;
      </span>
    </>
  );

  const pick = (r) => {
    onChange(r.value);
    if (onPick) onPick(r);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx(i => Math.min(rows.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx(i => Math.max(-1, i - 1)); }
    else if (e.key === "Enter" && idx >= 0 && rows[idx]) { e.preventDefault(); pick(rows[idx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        value={value || ""}
        onChange={e => handleChange(e.target.value)}
        onFocus={handleFocus}
        onKeyDown={onKey}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
        {...inputProps}
      />
      {open && (rows.length > 0 || loading) && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
             data-testid={`${testId}-dropdown`}>
          {loading && rows.length === 0 && (
            <div className="p-3 text-xs text-slate-400">Mencari…</div>
          )}
          {rows.map((r, i) => (
            <button type="button" key={`${r.value}-${i}`} onClick={() => pick(r)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 border-b border-slate-50 last:border-0 ${idx === i ? "bg-emerald-50" : "hover:bg-slate-50"}`}
              data-testid={`${testId}-item-${i}`}>
              {renderRow ? renderRow(r) : barisBawaan(r)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
